import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { Button, Dialog, TextareaAutosize, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { postRequest } from 'utils/api';
import { closeModal } from 'features/utilsSlice';
import { setActiveTab } from 'features/chatSlice';
import useModalOpen from 'hooks/useModalOpen';
import useModalData from 'hooks/useModalData';

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 10,
    padding: 20,
    width: '90%',
    maxWidth: 400,
    [theme.breakpoints.up('md')]: {
      width: 400,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  closeIcon: {
    cursor: 'pointer',
    padding: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      opacity: 0.7,
    },
  },
  textarea: {
    width: '100%',
    padding: '12px',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    fontFamily: 'inherit',
    fontSize: 14,
    resize: 'vertical',
    minHeight: 100,
    marginBottom: 15,
    '&:focus': {
      outline: 'none',
      borderColor: '#5b00db',
    },
  },
}));

const MODAL_NAME = 'Send Message';

const SendMessageDialog = () => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const classes = useStyles();
  const dispatch = useDispatch();
  const router = useRouter();

  const open = useModalOpen(MODAL_NAME);
  const { profileId, profileName } = useModalData(MODAL_NAME);

  const handleClose = () => {
    dispatch(closeModal(MODAL_NAME));
    setMessage('');
  };

  const handleSubmit = () => {
    if (!message.trim() || !profileId) return;

    setIsLoading(true);

    postRequest({
      endpoint: 'messages/direct',
      data: {
        recipient_id: profileId,
        recipient_type: 'Profile',
        body: message.trim(),
      },
    })
      .then(response => {
        setIsLoading(false);
        handleClose();
        dispatch(setActiveTab('dms'));
        router.push(`/chat?chatId=${response.data.conversation_id}&tab=dms`);
      })
      .catch(() => setIsLoading(false));
  };

  const disabled = !message.trim() || isLoading;

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ className: classes.paper }}>
      <div className={classes.header}>
        <h3 className={classes.title}>Message {profileName}</h3>
        <div className={classes.closeIcon} onClick={handleClose}>
          <Image src="/icons/close-main.svg" width={14} height={14} alt="Close" unoptimized />
        </div>
      </div>

      <TextareaAutosize
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Write your message..."
        className={classes.textarea}
        minRows={4}
        maxRows={8}
        autoFocus
      />

      <Button
        color="primary"
        variant="contained"
        onClick={handleSubmit}
        disabled={disabled}
        fullWidth
        style={{ height: 45 }}
      >
        {isLoading ? <CircularProgress color="inherit" size={24} /> : 'Send Message'}
      </Button>
    </Dialog>
  );
};

export default SendMessageDialog;
